import { FC, useMemo, useState } from 'react';
import { Avatar, Col, Row } from 'antd';
import {
    TbBuilding,
    TbBuildingBank,
    TbBuildingEstate,
    TbBuildingFactory2,
    TbDatabase,
    TbFileText,
    TbSearch,
} from 'react-icons/tb';

import { useAntDesignTheme } from '@/hooks/useAntDesignTheme';
import {
    createFilterableColumn,
    normalizeListFilterValues,
    type FilterableColumn,
    type ListFilterRule,
} from '@/layouts/02-Authenticated/layout/03-Content/routes/08-Relations/layout/03-Sections/views/02-Lists/routes/ListDetail/layout/03-FiltersRow/components/01-FilterDropdown';
import { WorkspaceResourceFilterControls } from '@/layouts/02-Authenticated/layout/03-Content/routes/08-Relations/layout/03-Sections/views/02-Lists/routes/ListDetail/layout/03-FiltersRow/components/WorkspaceResourceFiltersRow';
import { EmptySection, SearchFilterInput, SelectableOptionCard } from '@/shared';
import { PlatformAlert } from '@/shared/components/alerts';
import { ListViewColumnDataType, SetStateMethod } from '@/types';

import type { VantageMetadata } from '../../../../../types';
import {
    buildVantageQueryTemplates,
    VANTAGE_TEMPLATE_FIRM_TYPES,
    type VantageQueryTemplate,
    type VantageTemplateFirmType,
} from '../QueriesTab/templates';
import { CollapsibleSection } from '../VantageSettingsPrimitives';
import { compactSql, humanize } from '../VantageSettingsPrimitives/helpers';

interface QueryTemplatesTabProps {
    code: string;
    limit: number;
    metadata: VantageMetadata;
    metadataError: string | null;
    setCode: SetStateMethod<string>;
}

const FIRM_TYPE_ICONS: Record<VantageTemplateFirmType, JSX.Element> = {
    'Asset Manager / RIA': <TbBuildingBank />,
    'Family Office': <TbBuildingEstate />,
    'Hedge Fund': <TbBuilding />,
    Endowment: <TbBuildingBank />,
    'Corporate Development & M&A': <TbBuildingFactory2 />,
    'Private Equity / Venture Capital': <TbBuildingEstate />,
};

const matchesTemplateFilter = (template: VantageQueryTemplate, rule: ListFilterRule) => {
    const selectedValues = normalizeListFilterValues(rule.value).map(String);
    const templateValues = rule.field === 'firmType' ? [template.firmType] : template.schemas;
    const matches = selectedValues.some((selectedValue) =>
        templateValues.some((templateValue) => templateValue.toLowerCase() === selectedValue.toLowerCase()),
    );

    return rule.operator === 'notEquals' ? !matches : matches;
};

const QueryTemplatesTab: FC<QueryTemplatesTabProps> = ({ code, limit, metadata, metadataError, setCode }) => {
    const { token } = useAntDesignTheme();
    const [templateQuery, setTemplateQuery] = useState('');
    const [templateFilters, setTemplateFilters] = useState<ListFilterRule[]>([]);
    const accessibleSchemaNames = useMemo(() => new Set(metadata.tables.map((table) => table.name)), [metadata.tables]);
    const templates = useMemo(
        () =>
            buildVantageQueryTemplates(limit).filter((template) =>
                template.schemas.every((schema) => accessibleSchemaNames.has(schema)),
            ),
        [accessibleSchemaNames, limit],
    );
    const templateFilterColumns = useMemo<FilterableColumn[]>(
        () => [
            createFilterableColumn({
                allowMultipleValues: true,
                dataType: ListViewColumnDataType.SELECT,
                filterOperators: ['equals'],
                filterOptions: VANTAGE_TEMPLATE_FIRM_TYPES.map((firmType) => ({
                    icon: FIRM_TYPE_ICONS[firmType],
                    label: firmType,
                    value: firmType,
                })),
                icon: <TbBuildingBank style={{ color: token.colorTextTertiary }} />,
                key: 'firmType',
                label: 'Firm Type',
                order: 0,
            }),
            createFilterableColumn({
                allowMultipleValues: true,
                dataType: ListViewColumnDataType.SELECT,
                filterOperators: ['equals'],
                filterOptions: metadata.tables.map((table) => ({ label: humanize(table.name), value: table.name })),
                icon: <TbDatabase style={{ color: token.colorTextTertiary }} />,
                key: 'schema',
                label: 'Schema',
                order: 1,
            }),
        ],
        [metadata.tables, token.colorTextTertiary],
    );
    const filteredTemplates = useMemo(() => {
        const normalizedQuery = templateQuery.trim().toLowerCase();

        return templates.filter((template) => {
            const matchesSearch =
                !normalizedQuery ||
                [template.title, template.description, template.firmType, ...template.schemas].some((value) =>
                    value.toLowerCase().includes(normalizedQuery),
                );

            return matchesSearch && templateFilters.every((rule) => matchesTemplateFilter(template, rule));
        });
    }, [templateFilters, templateQuery, templates]);

    return (
        <div>
            {metadataError ? (
                <PlatformAlert type='warning' showIcon message={metadataError} style={{ margin: '1rem' }} />
            ) : null}
            <CollapsibleSection
                title='Query Templates'
                description='Start with governed analysis patterns designed for investment research and due diligence teams.'
                icon={<TbFileText />}
            >
                <div style={{ paddingBottom: '0.5rem' }}>
                    <SearchFilterInput
                        bordered
                        placeholder='Search Query Templates...'
                        value={templateQuery}
                        setValue={setTemplateQuery}
                    />
                </div>
                <WorkspaceResourceFilterControls
                    columns={templateFilterColumns}
                    docsLabel='Vantage query template filters'
                    emptyColumnsDescription='Template filter fields are unavailable.'
                    emptyHint='Filter templates by firm type or governed schema.'
                    filterableResourceLabel='Query Templates'
                    filters={templateFilters}
                    horizontalPadding={0}
                    onChange={(nextFilters) => setTemplateFilters(nextFilters ?? [])}
                    resourceLabel='the query template catalog'
                />
                {filteredTemplates.length ? (
                    VANTAGE_TEMPLATE_FIRM_TYPES.map((firmType) => {
                        const groupedTemplates = filteredTemplates.filter((template) => template.firmType === firmType);

                        if (!groupedTemplates.length) {
                            return null;
                        }

                        return (
                            <div key={firmType} style={{ marginTop: '1rem' }}>
                                <small style={{ color: token.colorTextTertiary }}>{firmType}</small>
                                <Row gutter={[8, 8]} style={{ marginTop: '0.5rem' }}>
                                    {groupedTemplates.map((template) => (
                                        <Col key={template.id} span={12}>
                                            <SelectableOptionCard
                                                icon={
                                                    <Avatar
                                                        shape='square'
                                                        size={28}
                                                        icon={FIRM_TYPE_ICONS[firmType]}
                                                        style={{
                                                            border: `1px solid ${token.colorBorder}`,
                                                            color: token.colorTextTertiary,
                                                        }}
                                                    />
                                                }
                                                label={template.title}
                                                description={template.description}
                                                selected={compactSql(code) === compactSql(template.sql)}
                                                onSelect={() => setCode(template.sql)}
                                            />
                                        </Col>
                                    ))}
                                </Row>
                            </div>
                        );
                    })
                ) : (
                    <EmptySection
                        height={160}
                        icon={<TbSearch size='1.25rem' />}
                        label='No Matching Templates'
                        description='Adjust your search or filters to explore the governed template catalog.'
                    />
                )}
            </CollapsibleSection>
        </div>
    );
};

export default QueryTemplatesTab;
